import React from 'react';
import {
  MousePointer,
  Plus,
  Move,
  RotateCw,
  Maximize2,
  PenTool,
  Ruler,
  Trash2,
  Undo2,
  Redo2,
  LayoutGrid,
  Box,
  ZoomOut,
  ZoomIn,
  Grid,
  Magnet,
  Save,
  Download,
  User,
  HelpCircle,
  Play,
  Pause,
  Hand,
} from 'lucide-react';
import { toast } from 'sonner';
import { useEditorStore, type EditorTool } from '@/core/stores/editor-store';
import { useHistoryStore } from '@/core/stores/history-store';
import { useStageStore } from '@/core/stores/stage-store';
import { smartSave } from '@/core/save';
import { MAX_ZOOM_PX_PER_M, zoomToPercent } from '@/canvas/coordinates';
import { IconButton } from '@/ui/shared';
import { ExportDialog } from '@/export';
import ShortcutsOverlay from '@/ui/ShortcutsOverlay';

const toolConfig: Array<{
  tool: EditorTool;
  icon: React.ReactNode;
  label: string;
  shortcut: string;
}> = [
  { tool: 'select', icon: <MousePointer size={18} />, label: 'Select', shortcut: 'S' },
  { tool: 'place', icon: <Plus size={18} />, label: 'Place', shortcut: 'P' },
  { tool: 'move', icon: <Move size={18} />, label: 'Move', shortcut: 'M' },
  { tool: 'rotate', icon: <RotateCw size={18} />, label: 'Rotate', shortcut: 'R' },
  { tool: 'scale', icon: <Maximize2 size={18} />, label: 'Scale', shortcut: 'C' },
  { tool: 'wall_draw', icon: <PenTool size={18} />, label: 'Draw Wall', shortcut: 'W' },
  { tool: 'measure', icon: <Ruler size={18} />, label: 'Measure', shortcut: 'T' },
  { tool: 'delete', icon: <Trash2 size={18} />, label: 'Delete', shortcut: 'D' },
];

const Toolbar: React.FC = () => {
  const {
    activeTool,
    setTool,
    viewMode,
    setViewMode,
    zoom,
    setZoom,
    showGrid,
    toggleGrid,
    snapToGrid,
    toggleSnapToGrid,
    isWalkthrough,
    toggleWalkthrough,
    is3DEditMode,
    toggleEditMode3D,
    isAnimating,
    toggleAnimating,
  } = useEditorStore();

  const { canUndo, canRedo, undo, redo } = useHistoryStore();
  const { isSaving, setLayout, stageId, layout } = useStageStore();

  const [exportOpen, setExportOpen] = React.useState(false);
  const [shortcutsOpen, setShortcutsOpen] = React.useState(false);

  return (
    <div className="h-12 bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700 flex items-center px-2 gap-1 shadow-sm">
      {/* Tool buttons */}
      <div className="flex items-center gap-0.5">
        {toolConfig.map((config) => (
          <IconButton
            key={config.tool}
            icon={config.icon}
            tooltip={`${config.label} (${config.shortcut})`}
            onClick={() => setTool(config.tool)}
            active={activeTool === config.tool}
            size="sm"
          />
        ))}
      </div>

      {/* Separator */}
      <div className="h-6 w-px bg-gray-300 dark:bg-gray-600 mx-1" />

      {/* Undo/Redo */}
      <div className="flex items-center gap-0.5">
        <IconButton
          icon={<Undo2 size={18} />}
          tooltip="Undo (Ctrl+Z)"
          onClick={() => {
            const snapshot = undo();
            if (snapshot) setLayout(snapshot);
          }}
          disabled={!canUndo}
          size="sm"
        />
        <IconButton
          icon={<Redo2 size={18} />}
          tooltip="Redo (Ctrl+Y)"
          onClick={() => {
            const snapshot = redo();
            if (snapshot) setLayout(snapshot);
          }}
          disabled={!canRedo}
          size="sm"
        />
      </div>

      {/* Separator */}
      <div className="h-6 w-px bg-gray-300 dark:bg-gray-600 mx-1" />

      {/* View mode */}
      <div className="flex items-center gap-0.5">
        <IconButton
          icon={<LayoutGrid size={18} />}
          tooltip="2D View"
          onClick={() => setViewMode('2d')}
          active={viewMode === '2d'}
          size="sm"
        />
        <IconButton
          icon={<Box size={18} />}
          tooltip="3D View"
          onClick={() => setViewMode('3d')}
          active={viewMode === '3d'}
          size="sm"
        />
        <IconButton
          icon={<User size={18} />}
          tooltip="Walkthrough (F)"
          onClick={() => toggleWalkthrough()}
          active={isWalkthrough}
          disabled={viewMode !== '3d'}
          size="sm"
        />
        <IconButton
          icon={<Hand size={18} />}
          tooltip="3D Edit Mode — freeze camera, drag/place/delete objects (E)"
          onClick={() => toggleEditMode3D()}
          active={is3DEditMode}
          disabled={viewMode !== '3d'}
          size="sm"
        />
        <IconButton
          icon={isAnimating ? <Pause size={18} /> : <Play size={18} />}
          tooltip={isAnimating ? 'Pause moving targets' : 'Play moving targets'}
          onClick={() => toggleAnimating()}
          active={isAnimating}
          disabled={viewMode !== '3d'}
          size="sm"
        />
      </div>

      {/* Separator */}
      <div className="h-6 w-px bg-gray-300 dark:bg-gray-600 mx-1" />

      {/* Zoom controls */}
      <div className="flex items-center gap-1">
        <IconButton
          icon={<ZoomOut size={18} />}
          tooltip="Zoom Out"
          onClick={() => setZoom(Math.max(1, zoom / 1.25))}
          size="sm"
        />
        <div className="px-2 py-1 text-xs font-mono text-gray-600 dark:text-gray-400 min-w-12 text-center">
          {zoomToPercent(zoom)}%
        </div>
        <IconButton
          icon={<ZoomIn size={18} />}
          tooltip="Zoom In"
          onClick={() => setZoom(Math.min(MAX_ZOOM_PX_PER_M, zoom * 1.25))}
          size="sm"
        />
      </div>

      {/* Separator */}
      <div className="h-6 w-px bg-gray-300 dark:bg-gray-600 mx-1" />

      {/* Grid and snap */}
      <div className="flex items-center gap-0.5">
        <IconButton
          icon={<Grid size={18} />}
          tooltip="Toggle Grid"
          onClick={() => toggleGrid()}
          active={showGrid}
          size="sm"
        />
        <IconButton
          icon={<Magnet size={18} />}
          tooltip="Snap to Grid"
          onClick={() => toggleSnapToGrid()}
          active={snapToGrid}
          size="sm"
        />
      </div>

      {/* Spacer */}
      <div className="flex-1" />

      {/* Save and export */}
      <div className="flex items-center gap-0.5">
        <IconButton
          icon={<Save size={18} />}
          tooltip={stageId ? 'Save to server (Ctrl+S)' : 'Save copy to file (Ctrl+S)'}
          onClick={smartSave}
          disabled={isSaving}
          size="sm"
        />
        <IconButton
          icon={<Download size={18} />}
          tooltip="Export (PDF / PNG / JSON)"
          onClick={() => setExportOpen(true)}
          disabled={!layout}
          size="sm"
        />
        <IconButton
          icon={<HelpCircle size={18} />}
          tooltip="Keyboard shortcuts (?)"
          onClick={() => setShortcutsOpen(true)}
          size="sm"
        />
      </div>

      <ShortcutsOverlay open={shortcutsOpen} onOpenChange={setShortcutsOpen} />

      {layout && (
        <ExportDialog
          layout={layout}
          open={exportOpen}
          onOpenChange={setExportOpen}
          onImportLayout={(imported) => {
            setLayout(imported);
            toast.success('Stage imported');
          }}
        />
      )}
    </div>
  );
};

export default Toolbar;
